import { Controller } from "@hotwired/stimulus";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

const PLATFORM_COLORS = {
  twitch: "#9146FF",
  youtube: "#FF0000",
  tiktok: "#FF0050",
  kick: "#53FC18",
  discord: "#5865F2",
  bluesky: "#0085FF",
  matrix: "#0DBD8B",
};
const FALLBACK_COLORS = ["#6366F1", "#F59E0B", "#10B981", "#EC4899", "#14B8A6"];

function tickColor() {
  const bc = getComputedStyle(document.documentElement).getPropertyValue("--bc").trim();
  return bc ? `hsl(${bc} / 0.5)` : "rgba(0,0,0,0.5)";
}

export default class extends Controller {
  static values = { snapshots: Array, label: { type: String, default: "Viewers" } };

  connect() {
    const series = this.snapshotsValue;
    if (!series?.length) {
      this.element.style.display = "none";
      return;
    }

    const totalPoints = series.reduce((n, s) => n + (s.snapshots?.length ?? 0), 0);
    if (totalPoints < 2) {
      this.element.style.display = "none";
      return;
    }

    // Build unified sorted timestamp axis (Unix seconds for uPlot)
    const allTs = [...new Set(series.flatMap((s) => s.snapshots.map((p) => p.recorded_at)))].sort();
    const xData = allTs.map((ts) => new Date(ts).getTime() / 1000);

    const multiPlatform = series.length > 1;
    const yData = series.map((s) => {
      const lookup = new Map(s.snapshots.map((p) => [p.recorded_at, p.count]));
      return allTs.map((ts) => lookup.get(ts) ?? null);
    });

    const color = tickColor();
    const w = this.element.offsetWidth || 400;
    const h = this.element.offsetHeight || 160;

    const opts = {
      width: w,
      height: h,
      padding: [8, 0, 0, 0],
      cursor: { sync: { key: "viewer-chart" } },
      series: [
        {},
        ...series.map((s, idx) => {
          const stroke =
            PLATFORM_COLORS[s.platform] ?? FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
          return {
            label: s.platform.charAt(0).toUpperCase() + s.platform.slice(1),
            stroke,
            fill: !multiPlatform ? stroke + "1A" : undefined,
            width: 2,
            spanGaps: true,
          };
        }),
      ],
      axes: [
        {
          stroke: color,
          grid: { show: false },
          ticks: { show: true, stroke: color },
          values: (u, vals) =>
            vals.map((v) => {
              if (v === null || v === undefined) return "";
              const d = new Date(v * 1000);
              return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
            }),
          space: 60,
        },
        {
          stroke: color,
          grid: { stroke: color + "33", width: 1 },
          ticks: { stroke: color },
        },
      ],
      legend: { show: multiPlatform },
      plugins: [],
    };

    // uPlot appends its own canvas into the target element.
    // Remove the placeholder canvas rendered by the Ruby component first.
    const placeholder = this.element.querySelector("canvas");
    if (placeholder) placeholder.remove();

    this._plot = new uPlot(opts, [xData, ...yData], this.element);

    this._ro = new ResizeObserver(() => {
      const newW = this.element.offsetWidth;
      if (newW > 0 && newW !== this._plot.width) this._plot.setSize({ width: newW, height: h });
    });
    this._ro.observe(this.element);
  }

  disconnect() {
    this._ro?.disconnect();
    this._plot?.destroy();
  }
}
